import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Plus } from 'lucide-react';
import type { ReactionEmoji } from '@/lib/db/types';

export const POST_REACTIONS: { emoji: ReactionEmoji; label: string }[] = [
  { emoji: '🦅', label: 'Great shot' },
  { emoji: '🔥', label: 'Heater' },
  { emoji: '🥃', label: 'Drinks on you' },
  { emoji: '🏌️', label: 'Challenge accepted' },
  { emoji: '🤝', label: 'Rematch' },
  { emoji: '😬', label: 'Oof' },
];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  myReactions: ReactionEmoji[];
  onToggle: (emoji: ReactionEmoji) => void;
}

export function PostReactionPicker({ open, onOpenChange, myReactions, onToggle }: Props) {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!open || !anchorRef.current) return;
    const update = () => {
      if (!anchorRef.current) return;
      const rect = anchorRef.current.getBoundingClientRect();
      setPos({ top: rect.top - 6, left: rect.left });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (anchorRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      onOpenChange(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open, onOpenChange]);

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        onClick={() => onOpenChange(!open)}
        className="flex items-center gap-1 px-2.5 py-2.5 text-sm font-semibold text-ink-soft"
        aria-expanded={open}
        aria-label="React to post"
      >
        <span className="emoji text-lg leading-none">😀</span>
        <Plus size={12} color="#475569" strokeWidth={2.5} />
      </button>
      {open && createPortal(
        <div
          ref={panelRef}
          className="fixed z-[200] flex max-w-[calc(100vw-16px)] gap-1 rounded-full border border-line bg-paper p-1.5 shadow-bt-md"
          style={{ top: pos.top, left: pos.left, transform: 'translateY(-100%)' }}
          role="menu"
        >
          {POST_REACTIONS.map((r) => (
            <button
              key={r.emoji}
              type="button"
              role="menuitem"
              title={r.label}
              onClick={() => {
                onToggle(r.emoji);
                onOpenChange(false);
              }}
              className={`bt-flat flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xl transition-colors duration-150 ${
                myReactions.includes(r.emoji) ? 'bg-gold/25 shadow-bt' : ''
              }`}
            >
              <span className="emoji">{r.emoji}</span>
            </button>
          ))}
        </div>,
        document.body,
      )}
    </>
  );
}
